import { create } from 'zustand';
import i18n from '../i18n/config';
import { CompleteGoogleRegistrationRequest, GoogleRegistration, User, LoginRequest, RegisterRequest } from '../types';
import { authService } from '../services/auth.service';
import toast from 'react-hot-toast';
import { setMonitoringUser } from '../monitoring/sentry';
import { HYDRATING } from '../utils/hydrating';
import { isTransientApiError } from '../utils/apiErrors';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (data: LoginRequest) => Promise<void>;
  loginWithGoogle: (credential: string, challenge: string) => Promise<void>;
  startGoogleRegistration: (credential: string, challenge: string) => Promise<GoogleRegistration>;
  completeGoogleRegistration: (data: CompleteGoogleRegistrationRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: (password?: string, reason?: string) => Promise<void>;
  loadUser: () => Promise<void>;
  updateUser: (user: User) => void;
  /** Reads the stored session. Called right after hydration (the first render is anonymous, like the prerendered HTML). */
  restoreFromStorage: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // zustand uses the initial state as getServerSnapshot, so a hydrating page renders anonymous first.
  user: HYDRATING ? null : authService.getUser(),
  isAuthenticated: HYDRATING ? false : authService.isAuthenticated(),

  restoreFromStorage: () => set({ user: authService.getUser(), isAuthenticated: authService.isAuthenticated() }),
  isLoading: false,

  login: async (data: LoginRequest) => {
    try {
      set({ isLoading: true });
      const response = await authService.login(data);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
      toast.success('Connexion réussie !');
    } catch (error: unknown) {
      set({ isLoading: false });
      throw error;
    }
  },

  loginWithGoogle: async (credential: string, challenge: string) => {
    try {
      set({ isLoading: true });
      const response = await authService.loginWithGoogle(credential, challenge);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
      toast.success(i18n.t('common.loginSuccess'));
    } catch (error: unknown) {
      set({ isLoading: false });
      throw error;
    }
  },

  startGoogleRegistration: async (credential: string, challenge: string) => {
    set({ isLoading: true });
    try {
      return await authService.startGoogleRegistration(credential, challenge);
    } finally {
      set({ isLoading: false });
    }
  },

  completeGoogleRegistration: async (data: CompleteGoogleRegistrationRequest) => {
    try {
      set({ isLoading: true });
      const response = await authService.completeGoogleRegistration(data);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
      toast.success(i18n.t('auth.registerSuccess'));
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (data: RegisterRequest) => {
    try {
      set({ isLoading: true });
      const response = await authService.register(data);
      set({ user: response.user, isAuthenticated: true, isLoading: false });
      toast.success('Inscription réussie ! Bienvenue sur Bricolat !');
    } catch (error: unknown) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    // Prevent multiple logout calls/toasts
    if (!get().isAuthenticated) return;

    try {
      await authService.logout();
      set({ user: null, isAuthenticated: false });
      toast.success('Déconnexion réussie');
    } catch (error: unknown) {
      console.error('Logout error:', error);
      // Force logout even if API call fails
      set({ user: null, isAuthenticated: false });
    }
  },

  deleteAccount: async (password?: string, reason?: string) => {
    await authService.deleteAccount(password, reason);
    // Update the store first so the 'auth:logout' listener (triggered by clearSession) is a no-op.
    set({ user: null, isAuthenticated: false });
    authService.clearSession();
  },

  loadUser: async () => {
    try {
      if (!authService.isAuthenticated()) {
        set({ user: null, isAuthenticated: false });
        return;
      }

      set({ isLoading: true });
      const user = await authService.getMe();
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      console.error('Failed to load user:', error);
      // An API outage (nightly pause, network) must not sign out a valid session: keep the restored user.
      if (isTransientApiError(error) && authService.isAuthenticated()) {
        set({ isAuthenticated: get().user !== null, isLoading: false });
        return;
      }
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  updateUser: (user: User) => {
    set({ user });
    authService.saveUser(user);
  },
}));

// Error/performance reports carry the internal account id and roles only (never email or name).
const syncMonitoringUser = (user: User | null): void =>
  setMonitoringUser(user ? { id: user.id, role: user.roles?.join(',') } : null);

syncMonitoringUser(useAuthStore.getState().user);
useAuthStore.subscribe((state, previous) => {
  if (state.user?.id !== previous.user?.id || state.user?.roles !== previous.user?.roles) {
    syncMonitoringUser(state.user);
  }
});
